/**
 * §13.3 — first 60 seconds. Non-modal overlays in the bottom third, each with a
 * measurable gate; a beat ends when its gate fires or its window closes, whichever
 * comes first. Replayable from Settings → Gameplay; skipped entirely for a profile
 * that has already seen it (`settings.gameplay.onboarding_seen`).
 *
 * Copy switches on the detected input method (DECISIONS AMD-1): a phone reads
 * "Drag to move", a keyboard reads "WASD", a pad reads "left stick".
 */

import { div, setText } from './dom';
import type { InputMethod } from './types';

export type OnboardingGate = 'moved' | 'dashed' | 'motes' | 'chose' | 'none';

interface Beat {
  id: string;
  start_s: number;
  end_s: number;
  gate: OnboardingGate;
  copy: Record<InputMethod, string>;
}

/** §13.3 table. Windows are in run seconds; gates are measured in the sim. */
export const BEATS: readonly Beat[] = [
  {
    id: 'move',
    start_s: 0,
    end_s: 10,
    gate: 'moved',
    copy: {
      touch: 'Drag anywhere to move.',
      keyboard: 'Move — WASD or the arrow keys.',
      gamepad: 'Move — left stick.',
    },
  },
  {
    id: 'dash',
    start_s: 10,
    end_s: 20,
    gate: 'dashed',
    copy: {
      touch: 'Double-tap to dash. You are untouchable during a dash.',
      keyboard: 'Dash — Space. You are untouchable during a dash.',
      gamepad: 'Dash — RB. You are untouchable during a dash.',
    },
  },
  {
    id: 'collect',
    start_s: 20,
    end_s: 40,
    gate: 'motes',
    copy: {
      touch: 'Your weapons fire on their own. Collect the green-cyan motes.',
      keyboard: 'Your weapons fire on their own. Collect the green-cyan motes.',
      gamepad: 'Your weapons fire on their own. Collect the green-cyan motes.',
    },
  },
  {
    id: 'choose',
    start_s: 40,
    end_s: 60,
    gate: 'chose',
    copy: {
      touch: 'Level up — tap a card. Reroll swaps all three, three times a run.',
      keyboard: 'Level up — press 1, 2 or 3. R rerolls, three times a run.',
      gamepad: 'Level up — D-pad and A. X rerolls, three times a run.',
    },
  },
  {
    id: 'close',
    start_s: 60,
    end_s: 66,
    gate: 'none',
    copy: {
      touch: 'Survive 20 minutes. Getting hit costs Lumensurge — stay bright.',
      keyboard: 'Survive 20 minutes. Getting hit costs Lumensurge — stay bright.',
      gamepad: 'Survive 20 minutes. Getting hit costs Lumensurge — stay bright.',
    },
  },
];

/** §13.3 gate thresholds. */
export const GATE_DISTANCE_U = 10;
export const GATE_MOTES = 5;

export class Onboarding {
  readonly root: HTMLElement;
  private textNode: HTMLElement;
  private progressNode: HTMLElement;
  private beatIndex = -1;
  private active = false;
  private method: InputMethod = 'keyboard';

  // gate counters, fed by UI event subscriptions and the frame path
  private distance = 0;
  private dashes = 0;
  private motes = 0;
  private choices = 0;
  private lastX = 0;
  private lastY = 0;
  private hasLast = false;

  constructor() {
    this.root = div('onboarding');
    this.root.hidden = true;
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');
    const card = div('onboarding-card');
    this.textNode = div('onboarding-text', '');
    this.progressNode = div('onboarding-progress', '');
    card.appendChild(this.textNode);
    card.appendChild(this.progressNode);
    this.root.appendChild(card);
  }

  setInputMethod(m: InputMethod): void {
    if (this.method === m) return;
    this.method = m;
    if (this.active && this.beatIndex >= 0) setText(this.textNode, BEATS[this.beatIndex].copy[m]);
  }

  get isActive(): boolean {
    return this.active;
  }

  /** Fresh profile or "Replay onboarding". */
  start(): void {
    this.active = true;
    this.beatIndex = -1;
    this.distance = 0;
    this.dashes = 0;
    this.motes = 0;
    this.choices = 0;
    this.hasLast = false;
    this.root.hidden = true;
  }

  /** Returning-player skip path: zero overlays, §13.3 acceptance. */
  skip(): void {
    this.active = false;
    this.beatIndex = -1;
    this.root.hidden = true;
  }

  noteDash(): void {
    this.dashes++;
  }

  noteMote(): void {
    this.motes++;
  }

  noteChoice(): void {
    this.choices++;
  }

  /** Accumulates travelled distance from the render-side mirror of player position. */
  noteMovement(x: number, y: number): void {
    if (this.hasLast) {
      const dx = x - this.lastX;
      const dy = y - this.lastY;
      this.distance += Math.sqrt(dx * dx + dy * dy);
    }
    this.lastX = x;
    this.lastY = y;
    this.hasLast = true;
  }

  private gateMet(gate: OnboardingGate): boolean {
    switch (gate) {
      case 'moved':
        return this.distance >= GATE_DISTANCE_U;
      case 'dashed':
        return this.dashes >= 1;
      case 'motes':
        return this.motes >= GATE_MOTES;
      case 'chose':
        return this.choices >= 1;
      default:
        return false;
    }
  }

  /** Called once per rendered frame with the run clock in seconds. */
  update(runSeconds: number): void {
    if (!this.active) return;

    let target = -1;
    for (let i = 0; i < BEATS.length; i++) {
      if (runSeconds >= BEATS[i].start_s && runSeconds < BEATS[i].end_s) {
        target = i;
        break;
      }
    }
    if (runSeconds >= BEATS[BEATS.length - 1].end_s) {
      this.active = false;
      this.root.hidden = true;
      return;
    }
    if (target < 0) {
      this.root.hidden = true;
      return;
    }

    if (target !== this.beatIndex) {
      this.beatIndex = target;
      setText(this.textNode, BEATS[target].copy[this.method]);
      this.root.hidden = false;
      this.root.classList.add('is-in');
    }

    const beat = BEATS[target];
    if (beat.gate !== 'none' && this.gateMet(beat.gate)) {
      if (!this.root.hidden) {
        this.root.hidden = true;
        this.root.classList.remove('is-in');
      }
      setText(this.progressNode, '');
      return;
    }
    setText(this.progressNode, this.progressText(beat.gate));
  }

  private progressText(gate: OnboardingGate): string {
    switch (gate) {
      case 'moved':
        return Math.min(GATE_DISTANCE_U, Math.floor(this.distance)) + ' / ' + GATE_DISTANCE_U;
      case 'motes':
        return Math.min(GATE_MOTES, this.motes) + ' / ' + GATE_MOTES;
      default:
        return '';
    }
  }
}
